import React, { useState, useEffect, useCallback } from 'react';
import { Settings2, CheckCircle2, XCircle, Loader2, Eye, EyeOff, Save, RefreshCw, Server, Lock, Upload, Sliders, Activity } from 'lucide-react';
import { apiService } from '../services/api';

interface CfConfig {
  accountId: string;
  workerName: string;
  kvNamespaceId: string;
  tokenSet: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

interface BackendConfig {
  proxyUrl: string;
  jwtSecretSet: boolean;
  workerSecretSet: boolean;
  updatedAt?: string;
  updatedBy?: string;
}

interface SystemConfig {
  maintenanceMode: boolean;
  maintenanceBanner: string;
  defaultModel: string;
  featureFlags: Record<string, boolean>;
  updatedAt?: string;
  updatedBy?: string;
}

const FEATURE_FLAG_LABELS: Record<string, string> = {
  memory_tier1: 'Memory Tier 1 (conversation history)',
  memory_tier2: 'Memory Tier 2 (insight tags)',
  telemetry: 'Telemetry collection',
  billing: 'Stripe billing',
  agentTools: 'Agent tools (code exec, web)',
  camera: 'Camera features',
};

type TestStatus = 'idle' | 'testing' | 'ok' | 'fail';
type SaveStatus = 'idle' | 'saving' | 'saved' | 'error';

export default function Settings() {
  // ── CF Worker config ────────────────────────────────────────────────────────
  const [cfg, setCfg] = useState<CfConfig | null>(null);
  const [cfLoading, setCfLoading] = useState(true);
  const [cfSaveStatus, setCfSaveStatus] = useState<SaveStatus>('idle');

  const [accountId, setAccountId]   = useState('');
  const [workerName, setWorkerName] = useState('');
  const [kvNsId, setKvNsId]         = useState('');
  const [apiToken, setApiToken]     = useState('');
  const [showCfToken, setShowCfToken] = useState(false);

  const [testStatus, setTestStatus] = useState<TestStatus>('idle');
  const [testMsg, setTestMsg]       = useState('');

  // ── CF Worker secrets ───────────────────────────────────────────────────────
  const [cfSecrets, setCfSecrets]           = useState<string[]>([]);
  const [cfSecretsError, setCfSecretsError] = useState<string>('');
  const [cfSecretsLoading, setCfSecretsLoading] = useState(false);
  const [secretValues, setSecretValues]     = useState<Record<string, string>>({});
  const [showSecrets, setShowSecrets]       = useState<Record<string, boolean>>({});
  const [secretPushStatus, setSecretPushStatus] = useState<Record<string, SaveStatus>>({});

  // ── Backend config ──────────────────────────────────────────────────────────
  const [backendCfg, setBackendCfg] = useState<BackendConfig | null>(null);
  const [backendLoading, setBackendLoading] = useState(true);
  const [backendSaveStatus, setBackendSaveStatus] = useState<SaveStatus>('idle');

  const [proxyUrl, setProxyUrl]               = useState('');
  const [jwtSecret, setJwtSecret]             = useState('');
  const [workerSecret, setWorkerSecret]       = useState('');
  const [showJwt, setShowJwt]                 = useState(false);
  const [showWs, setShowWs]                   = useState(false);

  // ── System config ───────────────────────────────────────────────────────────
  const [sysCfg, setSysCfg]           = useState<SystemConfig | null>(null);
  const [sysLoading, setSysLoading]   = useState(true);
  const [sysSaveStatus, setSysSaveStatus] = useState<SaveStatus>('idle');
  const [maintenanceMode, setMaintenanceMode] = useState(false);
  const [maintenanceBanner, setMaintenanceBanner] = useState('');
  const [defaultModel, setDefaultModel] = useState('');
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});

  // ── Runtime status ──────────────────────────────────────────────────────────
  const [runtimeStatus, setRuntimeStatus] = useState<any[] | null>(null);
  const [runtimeLoading, setRuntimeLoading] = useState(false);

  // ── Load functions ──────────────────────────────────────────────────────────
  const loadCf = useCallback(async () => {
    setCfLoading(true);
    try {
      const data = await apiService.getCfConfig();
      setCfg(data);
      setAccountId(data.accountId);
      setWorkerName(data.workerName);
      setKvNsId(data.kvNamespaceId);
    } catch { /* defaults */ }
    finally { setCfLoading(false); }
  }, []);

  const loadCfSecrets = useCallback(async () => {
    setCfSecretsLoading(true);
    setCfSecretsError('');
    try {
      const data = await apiService.listCfSecrets();
      setCfSecrets(data.secrets);
      if (data.error) setCfSecretsError(data.error);
    } catch (e: any) {
      setCfSecretsError(e?.response?.data?.error ?? e?.message ?? 'Failed to load secrets');
    } finally { setCfSecretsLoading(false); }
  }, []);

  const loadBackend = useCallback(async () => {
    setBackendLoading(true);
    try {
      const data = await apiService.getBackendConfig();
      setBackendCfg(data);
      setProxyUrl(data.proxyUrl);
    } catch { /* defaults */ }
    finally { setBackendLoading(false); }
  }, []);

  const loadSys = useCallback(async () => {
    setSysLoading(true);
    try {
      const data = await apiService.getSystemConfig();
      setSysCfg(data);
      setMaintenanceMode(data.maintenanceMode ?? false);
      setMaintenanceBanner(data.maintenanceBanner ?? '');
      setDefaultModel(data.defaultModel ?? '');
      setFeatureFlags(data.featureFlags ?? {});
    } catch { /* defaults */ }
    finally { setSysLoading(false); }
  }, []);

  const loadRuntimeStatus = useCallback(async () => {
    setRuntimeLoading(true);
    try {
      const data = await apiService.getRuntimeStatus();
      setRuntimeStatus(data.requirements ?? []);
    } catch { setRuntimeStatus([]); }
    finally { setRuntimeLoading(false); }
  }, []);

  useEffect(() => {
    void loadCf();
    void loadCfSecrets();
    void loadBackend();
    void loadSys();
    void loadRuntimeStatus();
  }, [loadCf, loadCfSecrets, loadBackend, loadSys, loadRuntimeStatus]);

  // ── CF Worker save ──────────────────────────────────────────────────────────
  const handleCfSave = async () => {
    setCfSaveStatus('saving');
    try {
      await apiService.saveCfConfig({
        accountId, workerName, kvNamespaceId: kvNsId,
        ...(apiToken ? { apiToken } : {}),
      });
      setApiToken('');
      setCfSaveStatus('saved');
      setTimeout(() => setCfSaveStatus('idle'), 3000);
      await loadCf();
      await loadCfSecrets(); // reload secrets with updated credentials
    } catch { setCfSaveStatus('error'); }
  };

  const handleTest = async () => {
    setTestStatus('testing');
    setTestMsg('');
    try {
      const result = await apiService.testCfConnection();
      if (result.success) {
        setTestStatus('ok');
        setTestMsg(result.workerFound ? `Worker found: ${result.workerFound}` : 'Connection OK');
      } else {
        setTestStatus('fail');
        setTestMsg(result.error ?? 'Unknown error');
      }
    } catch (e: any) {
      setTestStatus('fail');
      setTestMsg(e?.response?.data?.error ?? e?.message ?? 'Request failed');
    }
  };

  // ── CF Secrets push ─────────────────────────────────────────────────────────
  const handlePushSecret = async (name: string) => {
    const value = secretValues[name] ?? '';
    if (!value) return;
    setSecretPushStatus(s => ({ ...s, [name]: 'saving' }));
    try {
      await apiService.pushCfSecret(name, value);
      setSecretValues(s => ({ ...s, [name]: '' }));
      setSecretPushStatus(s => ({ ...s, [name]: 'saved' }));
      setTimeout(() => setSecretPushStatus(s => ({ ...s, [name]: 'idle' })), 3000);
      await loadCfSecrets();
    } catch (e: any) {
      setSecretPushStatus(s => ({ ...s, [name]: 'error' }));
    }
  };

  // ── Backend config save ─────────────────────────────────────────────────────
  const handleBackendSave = async () => {
    setBackendSaveStatus('saving');
    try {
      await apiService.saveBackendConfig({
        ...(proxyUrl       ? { proxyUrl }           : {}),
        ...(jwtSecret      ? { jwtSecret }           : {}),
        ...(workerSecret   ? { workerSharedSecret: workerSecret } : {}),
      });
      setJwtSecret('');
      setWorkerSecret('');
      setBackendSaveStatus('saved');
      setTimeout(() => setBackendSaveStatus('idle'), 3000);
      await loadBackend();
    } catch { setBackendSaveStatus('error'); }
  };

  // ── System config save ──────────────────────────────────────────────────────
  const handleSysSave = async () => {
    setSysSaveStatus('saving');
    try {
      await apiService.updateSystemConfig({ maintenanceMode, maintenanceBanner, defaultModel, featureFlags });
      setSysSaveStatus('saved');
      setTimeout(() => setSysSaveStatus('idle'), 3000);
      await loadSys();
    } catch { setSysSaveStatus('error'); }
  };

  // ── Dirty checks ────────────────────────────────────────────────────────────
  const cfDirty = accountId !== (cfg?.accountId ?? '') ||
    workerName !== (cfg?.workerName ?? '') ||
    kvNsId !== (cfg?.kvNamespaceId ?? '') ||
    apiToken !== '';
  const backendDirty = proxyUrl !== (backendCfg?.proxyUrl ?? '') || jwtSecret !== '' || workerSecret !== '';
  const sysDirty = sysCfg !== null && (
    maintenanceMode !== (sysCfg.maintenanceMode ?? false) ||
    maintenanceBanner !== (sysCfg.maintenanceBanner ?? '') ||
    defaultModel !== (sysCfg.defaultModel ?? '') ||
    JSON.stringify(featureFlags) !== JSON.stringify(sysCfg.featureFlags ?? {})
  );

  const CF_SECRETS = ['WORKER_SHARED_SECRET', 'FIREBASE_SERVICE_ACCOUNT_KEY'];

  return (
    <div className="space-y-8 max-w-2xl">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Settings</h1>
        <p className="mt-1 text-sm text-gray-500">Infrastructure configuration for the Aegis cloud</p>
      </div>

      {/* ── Cloudflare Worker Config ─────────────────────────────────────── */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Settings2 size={16} className="text-orange-500" />
          <h2 className="font-semibold text-gray-900">Cloudflare Worker</h2>
          {cfg?.tokenSet && (
            <span className="ml-auto text-xs text-green-600 font-medium flex items-center gap-1">
              <CheckCircle2 size={12} /> API token set
            </span>
          )}
        </div>
        {cfLoading ? (
          <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Account ID</label>
                <input value={accountId} onChange={e => setAccountId(e.target.value)}
                  placeholder="c5d80dad…"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Worker Name</label>
                <input value={workerName} onChange={e => setWorkerName(e.target.value)}
                  placeholder="aegis-cloud-proxy"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400" />
              </div>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">KV Namespace ID</label>
              <input value={kvNsId} onChange={e => setKvNsId(e.target.value)}
                placeholder="e2a5ff7d…"
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                API Token
                {cfg?.tokenSet && !apiToken && (
                  <span className="ml-2 text-gray-400 font-normal">(leave blank to keep existing)</span>
                )}
              </label>
              <div className="relative">
                <input type={showCfToken ? 'text' : 'password'} value={apiToken} onChange={e => setApiToken(e.target.value)}
                  placeholder={cfg?.tokenSet ? '••••••••••••••••••••••••' : 'Cloudflare API token…'}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-orange-400" />
                <button type="button" onClick={() => setShowCfToken(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showCfToken ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-400">
                Needs: <span className="font-mono">Workers Scripts:Edit</span> + <span className="font-mono">KV Storage:Edit</span>
              </p>
            </div>
            {cfg?.updatedAt && (
              <p className="text-xs text-gray-400">Last saved {new Date(cfg.updatedAt).toLocaleString()} by {cfg.updatedBy}</p>
            )}
            <div className="flex items-center gap-3 pt-2">
              <button onClick={handleCfSave} disabled={cfSaveStatus === 'saving' || !cfDirty}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-orange-500 text-white rounded-md hover:bg-orange-600 disabled:opacity-40">
                {cfSaveStatus === 'saving' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {cfSaveStatus === 'saving' ? 'Saving…' : 'Save'}
              </button>
              <button onClick={handleTest} disabled={testStatus === 'testing'}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium border border-gray-200 text-gray-700 rounded-md hover:bg-gray-50 disabled:opacity-40">
                {testStatus === 'testing' ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                Test connection
              </button>
              {cfSaveStatus === 'saved' && (
                <span className="flex items-center gap-1 text-sm text-green-600"><CheckCircle2 size={14} /> Saved</span>
              )}
            </div>
            {testStatus !== 'idle' && testStatus !== 'testing' && (
              <div className={`flex items-start gap-2 rounded-md px-3 py-2 text-sm ${testStatus === 'ok' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}>
                {testStatus === 'ok' ? <CheckCircle2 size={15} className="mt-0.5 shrink-0" /> : <XCircle size={15} className="mt-0.5 shrink-0" />}
                {testMsg}
              </div>
            )}
          </div>
        )}
      </div>

      {/* ── CF Worker Secrets ────────────────────────────────────────────── */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Lock size={16} className="text-purple-500" />
          <h2 className="font-semibold text-gray-900">CF Worker Secrets</h2>
          <button onClick={loadCfSecrets} disabled={cfSecretsLoading}
            className="ml-auto text-gray-400 hover:text-gray-600 disabled:opacity-40">
            <RefreshCw size={14} className={cfSecretsLoading ? 'animate-spin' : ''} />
          </button>
        </div>
        <div className="px-6 py-5 space-y-5">
          {cfSecretsError && !cfSecretsLoading && (
            <div className="flex items-center gap-2 text-xs text-amber-700 bg-amber-50 rounded-md px-3 py-2">
              <XCircle size={13} className="shrink-0" /> {cfSecretsError}
            </div>
          )}
          <p className="text-xs text-gray-500">
            These secrets are stored securely in Cloudflare and never leave the Worker runtime.
            Save the CF config above first so the API token is available.
          </p>
          {CF_SECRETS.map(name => {
            const isSet = cfSecrets.includes(name);
            const status = secretPushStatus[name] ?? 'idle';
            return (
              <div key={name} className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-mono font-medium text-gray-800">{name}</span>
                  {isSet
                    ? <span className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 size={12} /> Set</span>
                    : <span className="text-xs text-red-500 flex items-center gap-1"><XCircle size={12} /> Not set</span>
                  }
                </div>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <input
                      type={showSecrets[name] ? 'text' : 'password'}
                      value={secretValues[name] ?? ''}
                      onChange={e => setSecretValues(s => ({ ...s, [name]: e.target.value }))}
                      placeholder={isSet ? 'Enter new value to update…' : 'Enter value…'}
                      className="w-full border border-gray-200 rounded-md px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-purple-400"
                    />
                    <button type="button" onClick={() => setShowSecrets(s => ({ ...s, [name]: !s[name] }))}
                      className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                      {showSecrets[name] ? <EyeOff size={14} /> : <Eye size={14} />}
                    </button>
                  </div>
                  <button
                    onClick={() => handlePushSecret(name)}
                    disabled={!secretValues[name] || status === 'saving'}
                    className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium bg-purple-600 text-white rounded-md hover:bg-purple-700 disabled:opacity-40 whitespace-nowrap"
                  >
                    {status === 'saving' ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
                    {status === 'saving' ? 'Pushing…' : 'Push to CF'}
                  </button>
                </div>
                {status === 'saved' && (
                  <p className="text-xs text-green-600 flex items-center gap-1"><CheckCircle2 size={12} /> Pushed successfully</p>
                )}
                {status === 'error' && (
                  <p className="text-xs text-red-500 flex items-center gap-1"><XCircle size={12} /> Push failed — check CF API token permissions</p>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Backend Configuration ────────────────────────────────────────── */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Server size={16} className="text-blue-500" />
          <h2 className="font-semibold text-gray-900">Backend Configuration</h2>
          {backendCfg && (
            <div className="ml-auto flex items-center gap-2 text-xs">
              {backendCfg.jwtSecretSet
                ? <span className="text-green-600 flex items-center gap-1"><CheckCircle2 size={12} /> JWT set</span>
                : <span className="text-red-500 flex items-center gap-1"><XCircle size={12} /> JWT missing</span>}
              {backendCfg.workerSecretSet
                ? <span className="text-green-600 flex items-center gap-1"><CheckCircle2 size={12} /> WS set</span>
                : <span className="text-red-500 flex items-center gap-1"><XCircle size={12} /> WS missing</span>}
            </div>
          )}
        </div>
        {backendLoading ? (
          <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : (
          <div className="px-6 py-5 space-y-4">
            <p className="text-xs text-gray-500">
              These values are stored in Firestore and applied to the Cloud Function at runtime — no redeploy needed.
              Sensitive values (JWT, worker secret) are write-only and never returned.
            </p>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">CF Proxy URL</label>
              <input value={proxyUrl} onChange={e => setProxyUrl(e.target.value)}
                placeholder="https://edge.sharpai.org"
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400" />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                JWT Secret
                {backendCfg?.jwtSecretSet && !jwtSecret && (
                  <span className="ml-2 text-gray-400 font-normal">(leave blank to keep existing)</span>
                )}
              </label>
              <div className="relative">
                <input type={showJwt ? 'text' : 'password'} value={jwtSecret} onChange={e => setJwtSecret(e.target.value)}
                  placeholder={backendCfg?.jwtSecretSet ? '••••••••••••••••••••••••' : 'Random 64-char string…'}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400" />
                <button type="button" onClick={() => setShowJwt(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showJwt ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-400">Changing this will invalidate all existing user sessions.</p>
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Worker Shared Secret
                {backendCfg?.workerSecretSet && !workerSecret && (
                  <span className="ml-2 text-gray-400 font-normal">(leave blank to keep existing)</span>
                )}
              </label>
              <div className="relative">
                <input type={showWs ? 'text' : 'password'} value={workerSecret} onChange={e => setWorkerSecret(e.target.value)}
                  placeholder={backendCfg?.workerSecretSet ? '••••••••••••••••••••••••' : 'Shared secret for backend↔CF Worker auth…'}
                  className="w-full border border-gray-200 rounded-md px-3 py-2 pr-10 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-blue-400" />
                <button type="button" onClick={() => setShowWs(v => !v)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600">
                  {showWs ? <EyeOff size={15} /> : <Eye size={15} />}
                </button>
              </div>
              <p className="mt-1 text-xs text-gray-400">
                Must also be set as a CF Worker secret (WORKER_SHARED_SECRET) above to match.
              </p>
            </div>
            {backendCfg?.updatedAt && (
              <p className="text-xs text-gray-400">Last saved {new Date(backendCfg.updatedAt).toLocaleString()} by {backendCfg.updatedBy}</p>
            )}
            <div className="flex items-center gap-3 pt-2">
              <button onClick={handleBackendSave} disabled={backendSaveStatus === 'saving' || !backendDirty}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-40">
                {backendSaveStatus === 'saving' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {backendSaveStatus === 'saving' ? 'Saving…' : 'Save & Apply'}
              </button>
              {backendSaveStatus === 'saved' && (
                <span className="flex items-center gap-1 text-sm text-green-600"><CheckCircle2 size={14} /> Saved and applied</span>
              )}
              {backendSaveStatus === 'error' && (
                <span className="flex items-center gap-1 text-sm text-red-500"><XCircle size={14} /> Save failed</span>
              )}
            </div>
          </div>
        )}
      </div>
      {/* ── System Config ────────────────────────────────────────────────── */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Sliders size={16} className="text-indigo-500" />
          <h2 className="font-semibold text-gray-900">System Config</h2>
          {maintenanceMode && (
            <span className="ml-auto text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full font-medium">Maintenance ON</span>
          )}
        </div>
        {sysLoading ? (
          <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : (
          <div className="px-6 py-5 space-y-5">
            {/* Maintenance mode */}
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-800">Maintenance Mode</p>
                <p className="text-xs text-gray-400">Blocks all API requests and shows the banner to clients</p>
              </div>
              <button
                onClick={() => setMaintenanceMode(v => !v)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                  maintenanceMode ? 'bg-yellow-400' : 'bg-gray-200'
                }`}
              >
                <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
                  maintenanceMode ? 'translate-x-6' : 'translate-x-1'
                }`} />
              </button>
            </div>
            {maintenanceMode && (
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Banner message</label>
                <input
                  value={maintenanceBanner}
                  onChange={e => setMaintenanceBanner(e.target.value)}
                  placeholder="We'll be back shortly…"
                  className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500"
                />
              </div>
            )}
            {/* Default model */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">Default Model</label>
              <input
                value={defaultModel}
                onChange={e => setDefaultModel(e.target.value)}
                placeholder="gpt-4o-mini"
                className="w-full border border-gray-200 rounded-md px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-indigo-500"
              />
              <p className="mt-1 text-xs text-gray-400">Used when the client doesn't specify a model</p>
            </div>
            {/* Feature flags */}
            <div>
              <p className="text-xs font-medium text-gray-600 mb-3">Feature Flags</p>
              <div className="space-y-3">
                {Object.entries(FEATURE_FLAG_LABELS).map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between">
                    <span className="text-sm text-gray-700">{label}</span>
                    <button
                      onClick={() => setFeatureFlags(f => ({ ...f, [key]: !f[key] }))}
                      className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                        featureFlags[key] ? 'bg-indigo-600' : 'bg-gray-200'
                      }`}
                    >
                      <span className={`inline-block h-3.5 w-3.5 rounded-full bg-white shadow transition-transform ${
                        featureFlags[key] ? 'translate-x-4' : 'translate-x-0.5'
                      }`} />
                    </button>
                  </div>
                ))}
              </div>
            </div>
            {sysCfg?.updatedAt && (
              <p className="text-xs text-gray-400">Last saved {new Date(sysCfg.updatedAt).toLocaleString()} by {sysCfg.updatedBy}</p>
            )}
            <div className="flex items-center gap-3 pt-1">
              <button
                onClick={handleSysSave}
                disabled={sysSaveStatus === 'saving' || !sysDirty}
                className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:opacity-40"
              >
                {sysSaveStatus === 'saving' ? <Loader2 size={14} className="animate-spin" /> : <Save size={14} />}
                {sysSaveStatus === 'saving' ? 'Saving…' : 'Save'}
              </button>
              {sysSaveStatus === 'saved' && <span className="flex items-center gap-1 text-sm text-green-600"><CheckCircle2 size={14} /> Saved</span>}
              {sysSaveStatus === 'error' && <span className="flex items-center gap-1 text-sm text-red-500"><XCircle size={14} /> Save failed</span>}
            </div>
          </div>
        )}
      </div>

      {/* ── Runtime Status ───────────────────────────────────────────────── */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-100 flex items-center gap-2">
          <Activity size={16} className="text-teal-500" />
          <h2 className="font-semibold text-gray-900">Runtime Status</h2>
          <button onClick={loadRuntimeStatus} disabled={runtimeLoading} className="ml-auto text-gray-400 hover:text-gray-600 disabled:opacity-40">
            <RefreshCw size={14} className={runtimeLoading ? 'animate-spin' : ''} />
          </button>
        </div>
        {runtimeLoading ? (
          <div className="p-10 text-center text-gray-400 text-sm">Loading…</div>
        ) : !runtimeStatus ? null : (
          <div className="divide-y divide-gray-50">
            {runtimeStatus.map((r: any) => (
              <div key={r.id} className="flex items-start gap-3 px-6 py-3">
                <span className="mt-0.5 shrink-0">
                  {r.status === 'ok' && <CheckCircle2 size={15} className="text-green-500" />}
                  {r.status === 'warning' && <XCircle size={15} className="text-yellow-500" />}
                  {r.status === 'missing' && <XCircle size={15} className="text-red-500" />}
                  {r.status === 'unknown' && <Loader2 size={15} className="text-gray-400" />}
                </span>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-gray-800">{r.name}</p>
                  <p className="text-xs text-gray-400">{r.description}</p>
                  {r.detail && <p className="text-xs font-mono text-gray-500 mt-0.5">{r.detail}</p>}
                </div>
                <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                  r.status === 'ok' ? 'bg-green-100 text-green-700' :
                  r.status === 'warning' ? 'bg-yellow-100 text-yellow-700' :
                  r.status === 'missing' ? 'bg-red-100 text-red-700' :
                  'bg-gray-100 text-gray-500'
                }`}>{r.status}</span>
              </div>
            ))}
            {runtimeStatus.length === 0 && (
              <p className="px-6 py-8 text-center text-sm text-gray-400">No status data</p>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

